import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Compass, Loader2 } from 'lucide-react';
import { useTour } from '../components/tour/Tour';
import { useApp } from '../context/AppContext';
import { useT } from '../i18n';
import { getFacility, login } from '../services';
import { firebaseEnabled } from '../lib/firebase';
import type { SignInError } from '../context/AppContext';
import { useBrandColor, useFacilities } from '../hooks/useAppData';
import { facilityName, greetingDoctor } from '../lib/patient';
import { DEMO_MODE } from '../config/demo';
import { ClinicLogo, PoweredBy, SurakshaMark } from '../components/common/Brand';
import { LangToggle } from '../components/common/LangToggle';
import { ForgotPassword } from '../components/common/ForgotPassword';
import { LoginHero } from '../components/common/LoginHero';

const inputCls =
  'h-12 w-full rounded-xl border border-sc-line bg-white px-4 text-base text-slate-900 placeholder:text-slate-400 focus:border-sc-teal focus:outline-none focus:ring-2 focus:ring-sc-teal/30';

export default function Login() {
  const { slug } = useParams();
  const { lang, role, setRole, setFacilityId, login: markLoggedIn, signIn, signInWithCode, loggedIn, authReady } = useApp();
  const t = useT();
  const navigate = useNavigate();
  const { start: startTour } = useTour();
  const { data: facility } = useQuery({ queryKey: ['facility', slug], queryFn: () => getFacility(slug as string), enabled: !!slug });
  const { data: facilities = [] } = useFacilities();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 2-step login: after the password, the 6-digit code from the authenticator app.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  useBrandColor(facility?.brandColor);

  // "Practise first" links in the welcome emails: demo.surakshacall.com/login?tour=reception|doctor opens that role
  // with its guided tour already running. Demo only; the real app ignores it.
  const [params] = useSearchParams();
  const tourAsked = params.get('tour');
  const tourStarted = useRef(false);
  useEffect(() => {
    if (!DEMO_MODE || tourStarted.current || (tourAsked !== 'reception' && tourAsked !== 'doctor')) return;
    tourStarted.current = true;
    startTour(tourAsked);
  }, [tourAsked, startTour]);

  const clinicName = facility ? facilityName(facility, lang) : '';

  if (firebaseEnabled && authReady && loggedIn) return <Navigate to="/" replace />;

  const onCode = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signInWithCode(code);
      navigate('/');
    } catch (err) {
      setError(t(`login.err.${err as SignInError}`));
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return setError(t('login.required'));
    setError('');
    setBusy(true);
    if (firebaseEnabled) {
      try {
        await signIn(email, password);
        navigate('/');
      } catch (err) {
        if (err === 'needsCode') setNeedsCode(true);
        else setError(t(`login.err.${err as SignInError}`));
        setBusy(false);
      }
      return;
    }
    await login(email, password);
    if (facility) {
      setFacilityId(facility.id);
      if (role === 'super_admin') setRole('facility_admin');
    }
    markLoggedIn();
    navigate('/');
  };

  return (
    <div className="grid min-h-screen bg-sc-bg lg:grid-cols-[1.05fr_1fr]">
      <LoginHero clinic={facility?.shortName ?? 'Sharma Hospital'} doctor={greetingDoctor(facility?.doctors[0] ?? { title: 'Dr', name: 'Sharma' })} />
      <div className="flex flex-col px-5 py-6 sm:px-10">
        <div className="flex justify-end">
          <LangToggle />
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          {facility ? <ClinicLogo src={facility.logoUrl} alt={facility.displayNameEn} className="h-16 w-16" /> : <SurakshaMark className="h-16 w-16" />}
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="login-title">
            {facility ? t('login.clinicTitle', { clinic: clinicName }) : t('login.platformTitle')}
          </h1>
          <p className="mt-2 text-slate-600">{t('login.subtitle')}</p>

          {DEMO_MODE && (
            <div className="mt-6 rounded-2xl border-2 border-sc-teal bg-sc-soft p-4" data-testid="tour-start">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Compass className="h-4 w-4 text-sc-teal" />{t('tour.intro')}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <button type="button" onClick={() => startTour('doctor')} data-testid="tour-start-doctor" className="h-12 rounded-xl bg-sc-teal px-3 text-sm font-bold text-white hover:bg-sc-teal-hover">{t('tour.startDoctor')}</button>
                <button type="button" onClick={() => startTour('reception')} data-testid="tour-start-reception" className="h-12 rounded-xl border-2 border-sc-teal bg-white px-3 text-sm font-bold text-sc-teal">{t('tour.startReception')}</button>
              </div>
            </div>
          )}

          {needsCode ? (
            <form onSubmit={onCode} className="mt-8 space-y-5" noValidate data-testid="login-code-form">
              <div className="rounded-2xl bg-sc-soft p-4 text-sm text-teal-900">{t('twostep.loginHint')}</div>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('twostep.code')}</span>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={`${inputCls} text-center text-2xl tracking-[0.4em]`} data-testid="login-code-input" />
              </label>
              {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={busy || code.length !== 6} data-testid="login-code-submit" className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-sc-teal text-base font-semibold text-white disabled:opacity-60">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}{t('twostep.verify')}
              </button>
              <p className="text-sm text-slate-600">{t('twostep.lostPhone')}</p>
            </form>
          ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5" noValidate data-testid="login-form">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('login.email')}</span>
              <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('login.emailPlaceholder')} className={inputCls} data-testid="login-email-input" />
            </label>
            <label className="block">
              <span className="mb-1.5 flex items-center justify-between text-sm font-semibold text-slate-700">
                {t('login.password')}
                <button type="button" onClick={() => setForgotOpen(true)} className="text-sm font-semibold text-sc-teal hover:underline" data-testid="forgot-password-link">
                  {t('login.forgot')}
                </button>
              </span>
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('login.passwordPlaceholder')} className={inputCls} data-testid="login-password-input" />
            </label>
            {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-button" className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-sc-teal text-base font-semibold text-white transition-colors duration-200 hover:bg-sc-teal-hover disabled:opacity-70">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />}
              {busy ? t('login.submitting') : t('login.submit')}
            </button>
          </form>
          )}

          {DEMO_MODE && <p className="mt-6 rounded-xl border border-dashed border-amber-300 bg-sc-saffron-soft px-4 py-3 text-sm text-amber-900" data-testid="login-demo-hint">{t('login.demoHint')}</p>}

          {/* Demo only: a real login page must not list the platform's clinics. */}
          {!firebaseEnabled && <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('login.clinicLinks')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {facilities.map((f) => (
                <Link key={f.id} to={`/c/${f.id}/login`} data-testid={`clinic-login-link-${f.id}`} className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${slug === f.id ? 'border-sc-teal bg-sc-soft text-sc-teal' : 'border-sc-line bg-white text-slate-700 hover:border-sc-teal'}`}>
                  {f.shortName}
                </Link>
              ))}
              {slug && (
                <Link to="/login" data-testid="platform-login-link" className="rounded-full border border-sc-line bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-sc-teal">
                  {t('login.platformLink')}
                </Link>
              )}
            </div>
          </div>}
        </div>
        <div className="flex justify-center">
          <PoweredBy />
        </div>
      </div>
      <ForgotPassword open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
